import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Checkbox,
  ListItemText
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { deleteKeyDetail,linkVisitHistoryUpdate, linkTagMembersFetch } from "../Dossier/Network_call";
import { useParams } from "react-router-dom";

const Links = ({ onLinksChange, dossierData, refreshDossierData }) => {
  const { implId } = useParams();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [usefulLinks, setUsefulLinks] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [disabledDeleteButtons, setDisabledDeleteButtons] = useState({});
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [tagsToDelete, setTagsToDelete] = useState([]);

  useEffect(() => {
    if (dossierData && dossierData.links) {
      setUsefulLinks(dossierData.links);
    }
  }, [dossierData]);

  const formatUrl = (input) => {
    let url = input.trim();
    if (!/^(http|https):\/\//i.test(url)) {
      url = `https://${url}`;
    }
    const isValidUrl = /^(http|https):\/\/[^\s/$.?#].[^\s]*$/i.test(url);
    return isValidUrl ? url : null;
  };

  const handleOpen = () => {
    setId("");
    setName("");
    setLink("");
    setEditIndex(null);
    setIsSaveDisabled(false);
    setOpen(true);
    setSelectedTags([]);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTags([]);
    setTagsToDelete([]); 
  };


  const handleSave = () => {
    if (name && link) {
      const formattedLink = formatUrl(link);

      if (!formattedLink) {
        toast.error("Invalid URL format");
        return;
      }
      
      const newLink = { id, name, links: formattedLink,  tags: selectedTags, Tags_Delete: tagsToDelete,};


      let updatedLinks;
      if (editIndex !== null) {
        updatedLinks = usefulLinks.map((item, index) =>
          index === editIndex ? newLink : item
        );
      } else {
        updatedLinks = [...usefulLinks, newLink];
      }


      setUsefulLinks(updatedLinks);
      onLinksChange(updatedLinks);
      setTagsToDelete([]);
      setIsSaveDisabled(true);
      handleClose();
    } else {
      toast.error("Name and Link fields cannot be empty");
    }
  };

  const handleEdit = (index) => {
    const usefulLink = usefulLinks[index];
    setId(usefulLink.id);
    setName(usefulLink.name);
    setLink(usefulLink.links);
    setEditIndex(index);
    setIsSaveDisabled(false);
    setOpen(true);
    setSelectedTags(usefulLink.tags || []);
  };

  const handleDelete = async (index) => {
    const deleteLink = usefulLinks[index];
    const updatedLinks = usefulLinks.filter((_, i) => i !== index);
    try {
      setDisabledDeleteButtons((prev) => ({ ...prev, [index]: true }));

      await deleteKeyDetail({ id: { links: deleteLink.id } });

      setUsefulLinks(updatedLinks);
      onLinksChange(updatedLinks);
      refreshDossierData();

      setDisabledDeleteButtons((prev) => {
        const updatedState = { ...prev };
        delete updatedState[index];
        return updatedState;
      });

      toast.success("Link deleted successfully");
    } catch (error) {
      toast.error("Failed to delete Link");
      setDisabledDeleteButtons((prev) => ({ ...prev, [index]: false }));
    }
  };


  const handleLinkClick = async (index) => {
    const linkId = index;
    try {
     const response =  await linkVisitHistoryUpdate(linkId);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleLinkTagMembersFetch = async (implId) => {
    try {
      const response = await linkTagMembersFetch(implId);
      if (response.status === 200) {
  
        const uniqueMembers = Array.from(
          new Map(response.data.LinkTagUsers.map((member) => [member.id, member])).values()
        );
  
        setTags(uniqueMembers);
      } else {
        console.log("Error in Fetching Link Tag Members");
      }
    } catch (error) {
      toast.error(error);
    }
  };
  

  useEffect(() => {
    if (open) {
      handleLinkTagMembersFetch(implId); 
    }
  }, [open, implId]);

  const handleMemberToggle = (tag) => {
    setSelectedTags((prevSelectedTags) => {
      const isTagSelected = prevSelectedTags.some((selected) => selected.id === tag.id);
      
      if (isTagSelected) {
        setTagsToDelete((prevTagsToDelete) => [...prevTagsToDelete, tag]);
        return prevSelectedTags.filter((selected) => selected.id !== tag.id);
      }

      setTagsToDelete((prevTagsToDelete) =>
        prevTagsToDelete.filter((deletedTag) => deletedTag.id !== tag.id)
      );
      return [...prevSelectedTags, tag];
    });
  };
  
  

  return (
    <Box sx={{ p: 3, bgcolor: "#ffffff" }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: "bold", color: "#00A0B2" }}
      >
        Useful Links
      </Typography>
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{
          mb: 2,
          backgroundColor: "#1976d2",
          "&:hover": { backgroundColor: "#1565c0" },
        }}
      >
        Add Link
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Link</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usefulLinks.map((link, index) => (
              <TableRow key={index}>
                <TableCell>
                  <a
                    href={link.links}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleLinkClick(link.id)} 
                  >
                    {link.name}
                  </a>
                </TableCell>
                <TableCell>
                  {link.tags?.map((tag) => (
                    <Chip key={tag.id} label={tag.name} sx={{ mr: 1, backgroundColor:"#1FAFC2", borderRadius:"0.5rem", margin:"0.5rem" }} />
                  ))}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(index)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(index)}
                    disabled={disabledDeleteButtons[index]}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>
          {editIndex !== null ? "Edit Link" : "Add Link"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link"
                variant="outlined"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
              <InputLabel id="members-dropdown-label">
                Select Tags <span style={{ fontWeight: "normal", fontSize: "0.85em", color: "#888" }}>(Optional)</span>
              </InputLabel>
                <Select
                  labelId="members-dropdown-label"
                  multiple
                  value={selectedTags}
                  renderValue={(selected) =>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                      {selected.map((member) => (
                        <Chip key={member.id} label={member.name} sx={{backgroundColor:"#1976D2", borderRadius:"0.2rem", color:"#fff"}}/>
                      ))}
                    </Box>
                  }
                  label="Select Tags (Optional)"
                  open={isDropdownOpen}
                  onClose={() => setIsDropdownOpen(false)}
                  onOpen={() => setIsDropdownOpen(true)}
                >
                  <Box 
                    display="flex" 
                    justifyContent="space-between" 
                    alignItems="center" 
                    position="sticky" 
                    top={0} 
                    bgcolor="white" 
                    zIndex={1} 
                    marginLeft="1rem"
                    marginRight="1rem"
                    padding="1rem"
                  >
                    <Typography variant="h6">Select Tags</Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      Set
                    </Button>
                  </Box>
                  {tags.map((tag) => (
                    <MenuItem key={tag.id} value={tag}>
                      <Checkbox
                          checked={selectedTags.some((selected) => selected.id === tag.id)}
                          onChange={() => handleMemberToggle(tag)}
                        />
                      <ListItemText
                        primary={tag.name}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container justifyContent="space-between" sx={{ px: 2, py: 1 }}>
            <Button
              disabled={isSaveDisabled}
              onClick={handleSave}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              {editIndex !== null ? "Edit" : "Set"}
            </Button>
            <Button
              onClick={handleClose}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": { backgroundColor: "#c62828" },
              }}
            >
              Cancel
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Links;